import { useEffect, type ReactNode } from "react";
import { cn } from "@/shared/utils/cn";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: "bottom" | "left";
  heightClass?: string;
  widthClass?: string;
  children: ReactNode;
};

export function Sheet({
  open,
  onOpenChange,
  side = "bottom",
  heightClass = "h-[60vh]",
  widthClass = "w-3/4",
  children,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onOpenChange(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onOpenChange]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] transition-opacity duration-300",
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
      )}
    >
      <div data-testid="sheet-backdrop" className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
      <div
        className={cn(
          "absolute glass-strong shadow-glass transition-transform duration-300 ease-out",
          side === "bottom"
            ? cn("bottom-0 left-0 right-0 mx-auto max-w-md w-full rounded-t-3xl", heightClass, open ? "translate-y-0" : "translate-y-full")
            : cn("top-0 bottom-0 left-0 rounded-r-3xl", widthClass, "max-w-[320px]", open ? "translate-x-0" : "-translate-x-full"),
        )}
      >
        {children}
      </div>
    </div>
  );
}
